import { useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DataField, P } from "./common-components";
import { FormHeader } from "./FormHeader";
import { FormContextMA } from "../form-context";
import { ClinicalServiceTypes } from "../../patient/text/data";
import {
  formatPhoneDocumentation,
  formatToYYYY_MMM_DD,
} from "../../../helpers/functions";
import { FormFooter } from "./FormFooter";
import { getCurrentUserDetails } from "../../../redux/modules/user/actions";

export const CoverLetter = ({ referral, isReferral }) => {
  const { patient, clinicalServiceName, startDateTime, formType } =
    useContext(FormContextMA);

  const { currentLoggedInUserDetail } = useSelector((state) => state.currentLoggedInUserDetail);
  const usersState = useSelector((state) => state.users);
  const dispatch = useDispatch();

  // Helper to compute pharmacist full name once
  const getFullName = (u) => (u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : null);
  const pharmacistName =
    getFullName(currentLoggedInUserDetail) ??
    getFullName(usersState?.currentLoggedInUserDetail) ??
    "-";

  // Helper to format signature URL
  const getSignatureUrl = (u) => {
    if (!u?.signature || typeof u.signature !== 'string' || u.signature.length === 0) return null;
    return u.signature.startsWith('data:') ? u.signature : `data:image/png;base64,${u.signature}`;
  };
  const pharmacistSignature =
    getSignatureUrl(currentLoggedInUserDetail) ??
    getSignatureUrl(usersState?.currentLoggedInUserDetail) ??
    null;

  // Fetch user details if not available
  useEffect(() => {
    if (!currentLoggedInUserDetail) {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      const userId = userInfo.userId;
      
      if (userId) {
        dispatch(getCurrentUserDetails(userId, false));
      }
    }
  }, [currentLoggedInUserDetail, dispatch]);

  return (
    <>
      <FormHeader cover={true} formType={formType} />
      <div className="min-h-[800px] space-y-16">
        {/* patient details */}
        <div className="flex flex-col px-9 py-4">
          <div className="flex flex-row justify-between font-medium">
            <DataField
              value={`Minor Ailment Prescribing Service - Allergic Rhinitis`}
              className={"!text-[16px]"}
            />
            <P  className={"!text-[16px]"}>
              <span className="text-REELTeal">Service Date</span>{" "}
              {formatToYYYY_MMM_DD(startDateTime)}
            </P>
          </div>
        </div>
        {/* pharmacist details */}
        <div className="px-9 py-4 flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <DataField
              value={isReferral ? "Referral Type" : "Notification Type"}
              className={"!text-sm"}
            />
            <DataField
              value={
                referral &&
                `${referral.referralType} /${referral.designation}`.trim()
              }
            />
            {referral?.phoneNumber && (
              <DataField
                value={
                  referral?.phoneNumber
                    ? formatPhoneDocumentation(referral.phoneNumber)
                    : ""
                }
              />
            )}
            {referral?.fax && (
              <DataField
                value={
                  referral?.fax ? formatPhoneDocumentation(referral.fax) : ""
                }
              />
            )}
            {referral?.emailAddress && (
              <DataField value={referral?.emailAddress} />
            )}
          </div>
          <div className="space-y-2">
            {referral ? (
              <DataField
                value={`To: ${(
                  (referral.firstName || "") +
                  (referral.middleName ? ` ${referral.middleName}` : "") +
                  (referral.lastName ? ` ${referral.lastName}` : "")
                ).trim()}`}
              />
            ) : (
              <DataField value={`To: `} />
            )}
            <DataField
              label={`This document confirms a Minor Ailment Service for ${
                clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
                  ? "Allergic Rhinitis"
                  : "Annual Medication Review"
              } was provided at ${"Pharmacy Name"} for ${(
                (patient.firstName || "") +
                (patient.middleName ? ` ${patient.middleName}` : "") +
                (patient.lastName ? ` ${patient.lastName}` : "")
              ).trim()}. This document includes the results of the assessment for your records.`}
            />
          </div>
        </div>
        {/* footer details */}
        <div className="px-9 py-4 flex flex-col gap-2">
          <DataField label={"Sincerely,"} />
          <DataField label={"Prescribing Pharmacist Name"} value={pharmacistName} />
          <DataField
            className="flex-nowrap items-center"
            label={"Pharmacist Signature"}
            value={
              pharmacistSignature ? (
                <img
                  src={pharmacistSignature}
                  alt="Pharmacist Signature"
                  className="inline-block max-h-[40px] max-w-[200px] object-contain align-middle"
                />
              ) : (
                ""
              )
            }
          />
          <DataField label={formatToYYYY_MMM_DD(new Date().toISOString())} />
        </div>
      </div>
      <FormFooter />
    </>
  );
};
